import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import { NotificationsActive as NotificationsIcon, Close as CloseIcon } from '@mui/icons-material';
import { DIALOG_STYLES } from '../styles/dialogStyles';
import Txt from './design-system/Txt';
import { isCapacitorNative, isNativeAndroid, nativePushAvailable } from '../utils/capacitor';
import { enablePushOnThisDevice, accountPushDeviceCount, EnablePushResult } from '../utils/enablePush';
import { isTWA } from '../utils/platform';

/**
 * The value-first ask for notifications.
 *
 * It appears once, right after the user logs something — the moment the
 * coach has something to say back — never on a timer at page load. "Not now"
 * keeps it away for 14 days; a granted device keeps it away for good. The
 * Dashboard's quieter CoachReachCard covers the case where it was dismissed.
 */
interface NotificationPromptProps {
  onClose: () => void;
  onEnable: () => Promise<EnablePushResult>;
}

const DISMISSED_KEY = 'notificationPromptDismissed';
const SHOWN_THIS_SESSION = 'ft_notif_prompt_shown';
const DISMISS_DAYS = 14;

export const NotificationPrompt: React.FC<NotificationPromptProps> = ({ onClose, onEnable }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EnablePushResult | null>(null);

  const handleTurnOn = async () => {
    setBusy(true);
    const r = await onEnable();
    setBusy(false);
    setResult(r);
    if (r === 'granted') onClose();
  };

  const handleNotNow = () => {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    onClose();
  };

  const deniedHelp = isCapacitorNative() && !isNativeAndroid()
    ? 'Notifications are off for FuelTron in iOS Settings. Open Settings → FuelTron → Notifications, allow them, then tap Turn on again.'
    : isTWA() || isNativeAndroid()
      ? 'Notifications are off for the FuelTron app. Open Android Settings → Apps → FuelTron → Notifications, allow them, then tap Turn on again.'
      : 'Notifications are blocked for this site in your browser. Allow them in the browser\'s site settings, then tap Turn on again.';

  return (
    <Dialog open onClose={handleNotNow} {...DIALOG_STYLES.standard}>
      <DialogTitle sx={DIALOG_STYLES.title}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box className="icon-text-row" display="flex" alignItems="center" gap={1}>
            <NotificationsIcon sx={{ fontSize: 20, color: '#00E0FF', filter: 'drop-shadow(0 0 6px #00E0FF99)' }} />
            <Typography variant="h6" sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: 1 }}>
              Your coach's notes
            </Typography>
          </Box>
          <IconButton onClick={handleNotNow} size="small" sx={DIALOG_STYLES.closeButton} aria-label="Not now">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={DIALOG_STYLES.content}>
        <Txt sx={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
          Your coach reads your sleep, food and training, and writes you a short note when something stands out, plus one line about your week on Sunday. It also warns you when a logging streak is about to break.
        </Txt>
        <Txt sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: 'rgba(255,255,255,0.6)', mt: 1 }}>
          Nothing else. No marketing, and quiet hours are respected. You can switch it off any time in Settings.
        </Txt>
        {result === 'denied' && (
          <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8rem' }}>{deniedHelp}</Alert>
        )}
        {result === 'unsupported' && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.8rem' }}>This browser cannot receive notifications. Use the FuelTron app or Chrome on Android.</Alert>
        )}
        {result === 'failed' && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8rem' }}>Could not register this device. Try again in a moment.</Alert>
        )}
      </DialogContent>

      <DialogActions sx={DIALOG_STYLES.actions}>
        <Button onClick={handleNotNow} sx={DIALOG_STYLES.secondaryButton}>
          Not now
        </Button>
        <Button onClick={handleTurnOn} variant="contained" disabled={busy} sx={DIALOG_STYLES.primaryButton}>
          {busy ? 'Turning on…' : 'Turn on'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Decides when to show the prompt: the first food or workout logged in a
 * session, if the account has no device yet, and not within 14 days of a
 * "Not now". At most once per session.
 */
export const useNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onLogged = async () => {
      try {
        if (sessionStorage.getItem(SHOWN_THIS_SESSION) === '1') return;
        // Nothing to turn on in an Android app built without Firebase.
        if (isNativeAndroid() && !nativePushAvailable()) return;
        if (!localStorage.getItem('token')) return;
        const dismissed = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
        if (dismissed && Date.now() - dismissed < DISMISS_DAYS * 864e5) return;
        // Already receiving on this device → nothing to ask.
        if (isCapacitorNative()) {
          if (localStorage.getItem('ft_native_push_granted') === '1') return;
        } else if ('Notification' in window && Notification.permission === 'granted' && localStorage.getItem('fueltron_webpush_registered_at')) {
          return;
        }
        // Another device already receives the coach → the Settings list covers this one.
        const n = await accountPushDeviceCount();
        if (n && n > 0) return;
        sessionStorage.setItem(SHOWN_THIS_SESSION, '1');
        // A beat after the "logged" confirmation, not on top of it.
        timer = setTimeout(() => setShowPrompt(true), 1500);
      } catch { /* never break logging */ }
    };
    window.addEventListener('foodLogged', onLogged);
    window.addEventListener('exerciseLogged', onLogged);
    return () => {
      window.removeEventListener('foodLogged', onLogged);
      window.removeEventListener('exerciseLogged', onLogged);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleEnable = useCallback(() => enablePushOnThisDevice(), []);

  return { showPrompt, setShowPrompt, handleEnable };
};
